import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { getFamilySiteLucideIcon } from '@/lib/family-site-icons';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { AdminFamilySite, AdminFamilySiteList } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { GripVertical, Network, Plus, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

const QUERY_KEY = ['admin', 'family-sites'] as const;

export function AdminFamilySitesList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [query, setQuery] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: api.adminFamilySiteList,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.adminFamilySiteDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      qc.invalidateQueries({ queryKey: ['family-sites'] });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => api.adminFamilySiteReorder({ ids }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      qc.invalidateQueries({ queryKey: ['family-sites'] });
    },
    onError: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const items = data?.sites ?? [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((s) => s.name.toLowerCase().includes(q) || s.url.toLowerCase().includes(q));
  }, [items, query]);

  // 검색 중일 때는 드래그 정렬 비활성 (필터된 부분 순서 변경은 의미 없음).
  const dragEnabled = query.trim().length === 0;

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((s) => s.id === active.id);
    const newIndex = items.findIndex((s) => s.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(items, oldIndex, newIndex);
    qc.setQueryData<AdminFamilySiteList>(QUERY_KEY, { sites: reordered });
    reorderMutation.mutate(reordered.map((s) => s.id));
  }

  return (
    <PageShell
      header={
        <PageHeader
          title="패밀리 사이트"
          count={items.length}
          description="사용자 채팅 사이드바 상단에 노출되는 외부/사내 사이트 바로가기 목록. 좌측 핸들로 순서를 바꿀 수 있습니다."
          actions={
            <Button
              onClick={() => navigate({ to: '/admin/family-sites/$id', params: { id: 'new' } })}
            >
              <Plus className="size-4" />
              사이트 추가
            </Button>
          }
        />
      }
    >
      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : '목록을 불러오지 못했습니다.'}
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          onCreate={() => navigate({ to: '/admin/family-sites/$id', params: { id: 'new' } })}
        />
      ) : (
        <div className="space-y-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-foreground-muted" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="이름·URL 로 검색"
              className="pl-9"
            />
          </div>
          <div className="rounded-2xl border border-border bg-surface overflow-hidden">
            <div className="grid grid-cols-[40px_56px_minmax(140px,1fr)_minmax(180px,2fr)_72px_56px] items-center gap-2 px-3 py-2 border-b border-border bg-surface-muted/40 text-[11px] uppercase tracking-wider text-foreground-muted">
              <span />
              <span>로고</span>
              <span>이름</span>
              <span>URL</span>
              <span className="text-center">새 탭</span>
              <span />
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext
                items={filtered.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                {filtered.map((site) => (
                  <FamilySiteRow
                    key={site.id}
                    site={site}
                    dragEnabled={dragEnabled}
                    onOpen={() =>
                      navigate({
                        to: '/admin/family-sites/$id',
                        params: { id: site.id },
                      })
                    }
                    onDelete={() => {
                      if (window.confirm(`"${site.name}" 을 삭제할까요?`)) {
                        deleteMutation.mutate(site.id);
                      }
                    }}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {filtered.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-foreground-muted">
                검색 결과가 없습니다.
              </div>
            ) : null}
          </div>
        </div>
      )}
    </PageShell>
  );
}

interface FamilySiteRowProps {
  site: AdminFamilySite;
  dragEnabled: boolean;
  onOpen: () => void;
  onDelete: () => void;
}

function FamilySiteRow({ site, dragEnabled, onOpen, onDelete }: FamilySiteRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: site.id,
    disabled: !dragEnabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="grid grid-cols-[40px_56px_minmax(140px,1fr)_minmax(180px,2fr)_72px_56px] items-center gap-2 px-3 py-2 border-b border-border last:border-b-0 hover:bg-surface-muted/30"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label="순서 변경"
        title={dragEnabled ? '드래그해서 순서 변경' : '검색 중에는 정렬할 수 없습니다'}
        disabled={!dragEnabled}
        className={cn(
          'size-8 inline-grid place-items-center rounded',
          dragEnabled
            ? 'cursor-grab text-foreground-muted hover:bg-surface hover:text-foreground active:cursor-grabbing'
            : 'text-foreground-muted/40 cursor-not-allowed',
        )}
      >
        <GripVertical className="size-4" />
      </button>
      <button
        type="button"
        onClick={onOpen}
        className="size-9 rounded-md border border-border bg-surface-muted/40 grid place-items-center overflow-hidden"
        aria-label="수정"
      >
        <LogoPreview site={site} />
      </button>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: 행 전체 클릭으로 상세 */}
      <div onClick={onOpen} className="cursor-pointer">
        <div className="font-medium truncate">{site.name}</div>
      </div>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: same */}
      <div
        onClick={onOpen}
        className="cursor-pointer font-mono text-xs text-foreground-muted truncate"
      >
        {site.url}
      </div>
      <div className="text-center text-xs text-foreground-muted">
        {site.openInNewTab ? '✓' : '-'}
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label="삭제"
          className="size-8 inline-grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-destructive"
        >
          <Trash2 className="size-4" />
        </button>
      </div>
    </div>
  );
}

function LogoPreview({ site }: { site: AdminFamilySite }) {
  if (site.logoType === 'emoji' && site.logoEmoji) {
    return <span className="text-xl leading-none">{site.logoEmoji}</span>;
  }
  if (site.logoType === 'lucide') {
    const Icon = getFamilySiteLucideIcon(site.logoLucideName);
    if (Icon) return <Icon className="size-5 text-foreground" />;
  }
  if (site.logoType === 'image' && site.logoUrl) {
    return <img src={site.logoUrl} alt="" className="size-full object-contain" />;
  }
  return <Network className="size-4 text-foreground-muted" />;
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-surface/50 px-8 py-16 text-center">
      <Network className="size-8 mx-auto text-foreground-muted" />
      <h3 className="mt-3 text-base font-medium">등록된 패밀리 사이트가 없습니다.</h3>
      <p className="mt-1 text-sm text-foreground-muted">
        사이트를 추가하면 사용자 채팅 사이드바 상단에 표시됩니다.
      </p>
      <Button onClick={onCreate} className="mt-4">
        <Plus className="size-4" />
        사이트 추가
      </Button>
    </div>
  );
}
